import { useMemo, useState } from 'react';
import { CircleDollarSign, Clock3, Film, Image, RotateCcw, SlidersHorizontal, X } from 'lucide-react';
import ModelIcon from './ModelIcon.jsx';

const ranges = [
  { id: 'today', label: '今天' },
  { id: 'yesterday', label: '昨天' },
  { id: '7days', label: '近 7 天' },
];

function itemDate(item) {
  return new Date(item.createdAt || item.created_at || item.time || 0);
}

function recordTime(item) {
  const value = item.createdAt || item.created_at || item.time;
  return value ? new Date(value).toLocaleString('zh-CN', { hour12: false }) : '-';
}

function normalizeModel(item) {
  const meta = item.meta || item.metadata || {};
  const source = String(meta.model || item.model || meta.modelId || meta.modelName || item.reason || '').toLowerCase();
  if (source.includes('midjourney') || /\bmj\s*v?\d/.test(source)) return { id: 'midjourney', name: 'MJ', kind: 'image' };
  if (source.includes('nb-pro') || source.includes('banana pro') || source.includes('香蕉pro') || source.includes('香蕉 pro')) return { id: 'nb-pro', name: '香蕉 PRO', kind: 'image' };
  if (source.includes('nb-v2') || source.includes('banana 2') || source.includes('香蕉2') || source.includes('香蕉 2')) return { id: 'nb-v2', name: '香蕉 2', kind: 'image' };
  if (source.includes('gpt-images-2') || source.includes('gpt images 2') || source.includes('gpt image')) return { id: 'gpt-images-2', name: 'GPT Images 2', kind: 'image' };
  if (source.includes('seedance') || source.includes('video') || source.includes('视频')) return { id: 'video', name: '视频生成', kind: 'video' };
  return null;
}

function inRange(item, range) {
  const date = itemDate(item);
  if (Number.isNaN(date.getTime())) return false;
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
  if (range === 'today') return date >= today && date < tomorrow;
  if (range === 'yesterday') {
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    return date >= yesterday && date < today;
  }
  const sevenDaysAgo = new Date(today); sevenDaysAgo.setDate(today.getDate() - 6);
  return date >= sevenDaysAgo && date < tomorrow;
}

function recordIcon(item, normalized, delta) {
  const text = `${item.reason || ''} ${item.type || ''}`.toLowerCase();
  if (normalized?.kind === 'video') return <Film size={22} />;
  if (normalized?.id) return <ModelIcon modelId={normalized.id} />;
  if (text.includes('admin') || text.includes('后台') || text.includes('调整')) return <SlidersHorizontal size={22} />;
  if (delta >= 0 || text.includes('refund') || text.includes('退')) return <RotateCcw size={22} />;
  return <Image size={22} />;
}

export default function ConsumptionDialog({ open, onClose, ledger = [] }) {
  const [range, setRange] = useState('today');
  const records = useMemo(() => [...ledger]
    .filter((item) => inRange(item, range))
    .sort((a, b) => itemDate(b) - itemDate(a)), [ledger, range]);
  if (!open) return null;
  const total = records.filter((item) => Number(item.delta) < 0).reduce((sum, item) => sum + Math.abs(Number(item.delta)), 0);

  return (
    <div className="consumption-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
      <section className="consumption-dialog" role="dialog" aria-modal="true" aria-label="消费记录" onMouseLeave={onClose}>
        <header><span className="consumption-title-icon"><CircleDollarSign size={22} /></span><div><h2>消费记录</h2><p>模型使用与积分变动明细</p></div><button type="button" className="icon-button" onClick={onClose} aria-label="关闭"><X size={19} /></button></header>
        <div className="consumption-filter-bar" role="group" aria-label="记录时间范围">
          {ranges.map((item) => <button key={item.id} type="button" className={range === item.id ? 'is-active' : ''} onClick={() => setRange(item.id)}>{item.label}</button>)}
        </div>
        <div className="consumption-summary"><span>累计消耗</span><strong>{total.toFixed(2)} 积分</strong><small>{records.length} 条记录</small></div>
        <div className="consumption-list">
          {records.length === 0 ? <div className="consumption-empty">该时段暂无消费记录</div> : records.map((item, index) => {
            const meta = item.meta || item.metadata || {};
            const normalized = normalizeModel(item);
            const delta = Number(item.delta || 0);
            const resolution = meta.resolution || item.resolution;
            const count = Number(meta.count || meta.imageCount || item.count || 0);
            const details = [resolution, count > 0 ? `${count} 张` : '', delta < 0 ? '扣费成功' : '积分退回'].filter(Boolean).join(' · ');
            const text = `${item.reason || ''} ${item.type || ''}`.toLowerCase();
            const fallbackName = text.includes('admin') || text.includes('后台') || text.includes('调整') ? '管理操作' : delta >= 0 ? '积分退回' : '图片生成';
            return <article key={item.id || `${recordTime(item)}-${index}`}>
              <span className={`consumption-model-icon ${delta >= 0 ? 'is-refund' : ''}`}>{recordIcon(item, normalized, delta)}</span>
              <div className="consumption-record-copy"><strong>{normalized?.name || fallbackName}</strong><span>{details}</span></div>
              <strong className={delta < 0 ? 'is-debit' : 'is-credit'}>{delta < 0 ? '-' : '+'}{Math.abs(delta).toFixed(2)}</strong>
              <time><Clock3 size={13} />{recordTime(item)}</time>
            </article>;
          })}
        </div>
      </section>
    </div>
  );
}
